import { useState, useEffect, Suspense, lazy } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { InnovationFunnelWidget } from "../components/impacto/InnovationFunnelWidget";
import InnovationCycleFunnel from "../components/impacto/InnovationCycleFunnel";
import CostByBusinessLine from "./finanzas/CostByBusinessLine";
import { toast } from "sonner";
import {
  BarChart3, Filter, TrendingUp, DollarSign, Users, Building2, Award, Target,
  Rocket, Sparkles, Save, Plus, Trash2, PencilLine, CheckCircle2, XCircle,
  Newspaper, GraduationCap, ClipboardCheck, Loader2, ArrowRight, Brain, MinusCircle,
  TrendingDown, Minus, Moon, Clock, Scale, Workflow,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, LineChart, Line, Legend, ComposedChart, Area,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n) || 0);
const num = (n) => new Intl.NumberFormat("es-ES").format(Number(n) || 0);
const XVPage = lazy(() => import("./XVPage"));

// ─── Widget: última retrospectiva VAL ───────────────────────────────────────

const CONSISTENCY_STYLES = {
  estable:  { bg: "bg-emerald-50 dark:bg-emerald-950/30", text: "text-emerald-700 dark:text-emerald-300", ring: "ring-emerald-200/50", label: "Estable",  hint: "Hit-rate poco volátil" },
  moderado: { bg: "bg-amber-50 dark:bg-amber-950/30",     text: "text-amber-700 dark:text-amber-300",     ring: "ring-amber-200/50",   label: "Moderado", hint: "Variabilidad media" },
  erratico: { bg: "bg-red-50 dark:bg-red-950/30",         text: "text-red-700 dark:text-red-300",         ring: "ring-red-200/50",     label: "Errático", hint: "Alta volatilidad WoW" },
};

function ConsistencyBadge({ consistency }) {
  if (!consistency || consistency.hit_rate_stdev === null || consistency.hit_rate_stdev === undefined) {
    return (
      <div className="text-center min-w-[80px]" data-testid="retro-widget-consistency">
        <div className="text-2xl font-black leading-none text-slate-400">—</div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Consistencia</div>
        <div className="text-[10px] text-muted-foreground">Necesita ≥3 sem.</div>
      </div>
    );
  }
  const s = CONSISTENCY_STYLES[consistency.label] || CONSISTENCY_STYLES.moderado;
  return (
    <div className="text-center min-w-[90px]" data-testid="retro-widget-consistency" title={s.hint}>
      <div className={`text-2xl font-black leading-none ${s.text}`}>σ {consistency.hit_rate_stdev}</div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Consistencia</div>
      <div className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full mt-0.5 ${s.bg} ${s.text} ring-1 ${s.ring}`}>
        {s.label} · {consistency.sample_size}sem
      </div>
    </div>
  );
}

function DeltaChip({ value, unit = "", positiveIsGood = true }) {
  if (value === null || value === undefined) return null;
  const zero = value === 0;
  const good = zero ? null : (value > 0) === positiveIsGood;
  const cls = zero
    ? "bg-slate-100 text-slate-500"
    : good ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700";
  const Icon = zero ? Minus : value > 0 ? TrendingUp : TrendingDown;
  const formatted = Number.isInteger(value) ? `${value > 0 ? "+" : ""}${value}` : `${value > 0 ? "+" : ""}${value.toFixed(1)}`;
  return (
    <span className={`inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}
          data-testid="retro-widget-delta">
      <Icon className="w-3 h-3" />{formatted}{unit}
    </span>
  );
}

function RetroWidget({ token, onOpenTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/assistant/retro/latest`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error("HTTP " + r.status);
        const j = await r.json();
        if (!cancelled) { setData(j); setError(false); }
      } catch (e) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) return null;

  if (error) {
    return (
      <Card className="border-amber-200/60 bg-amber-50/40 dark:bg-amber-950/10 dark:border-amber-800/40"
            data-testid="retro-widget-error">
        <CardContent className="p-4 flex items-center gap-3 text-sm">
          <XCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span className="text-amber-800 dark:text-amber-300">
            No se pudo cargar la última retrospectiva VAL. El endpoint <code className="text-xs bg-amber-100 dark:bg-amber-900/40 px-1 rounded">/api/assistant/retro/latest</code> no respondió.
          </span>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const s = data.snapshot || {};
  const c = s.comparison || {};
  const hr = s.hit_rate_pct ?? 0;
  const hitColor = hr >= 60 ? "text-emerald-600" : hr >= 40 ? "text-amber-600" : "text-red-600";
  const genAt = data.generated_at ? new Date(data.generated_at) : null;
  const ago = genAt ? Math.floor((Date.now() - genAt.getTime()) / 86400000) : null;
  const agoStr = ago === null ? "" : ago === 0 ? "hoy" : ago === 1 ? "ayer" : `hace ${ago} días`;
  const noDecisions = (s.decisions_this_week ?? 0) === 0;

  return (
    <Card className="border-purple-200/60 dark:border-purple-800/40 bg-gradient-to-br from-purple-50/60 via-white to-fuchsia-50/40 dark:from-purple-950/20 dark:via-slate-900 dark:to-fuchsia-950/10"
          data-testid="retro-widget">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-start gap-4 justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-600/10 flex items-center justify-center">
              <Brain className="w-5 h-5 text-purple-700 dark:text-purple-300" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-purple-700 dark:text-purple-300 font-bold">
                Última retrospectiva VAL
              </div>
              <div className="text-sm text-muted-foreground flex items-center gap-2 mt-0.5">
                <Clock className="w-3 h-3" />
                {data.has_stored
                  ? <>Enviada {agoStr} · <span className="italic">{data.trigger === "scheduled" ? "automática (viernes)" : data.trigger?.startsWith("manual") ? "manual" : data.trigger}</span></>
                  : <>Snapshot en vivo · sin envío previo</>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="text-center min-w-[100px]" data-testid="retro-widget-hitrate">
              <div className={`text-2xl font-black leading-none ${hitColor}`}>
                {hr}%<DeltaChip value={c.hit_rate_delta_pp} unit=" pp" />
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                Hit-rate acumulado
              </div>
              <div className="text-[10px] text-muted-foreground">
                {s.proposals_hit ?? 0}/{s.proposals_scored ?? 0} propuestas
              </div>
            </div>

            <div className="text-center min-w-[80px]" data-testid="retro-widget-decisions">
              <div className="text-2xl font-black leading-none text-purple-700 flex items-center justify-center">
                {noDecisions ? <Moon className="w-5 h-5 text-slate-400" /> : (s.decisions_this_week ?? 0)}
                <DeltaChip value={c.decisions_delta !== null && c.decisions_delta !== undefined ? Math.round(c.decisions_delta) : null} />
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                Decisiones sem.
              </div>
              <div className="text-[10px] text-muted-foreground">
                {eur(s.total_moved_week_eur ?? 0)} movidos
              </div>
            </div>

            <ConsistencyBadge consistency={s.consistency} />

            <Button variant="outline" size="sm" onClick={() => onOpenTab?.("val")} data-testid="retro-widget-cta">
              Ver detalle <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── ROI horas: ranking de €/hora invertida por iniciativa ─────────────────
function RoiPerHourView({ authH }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [days, setDays] = useState(90);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErrorMsg(null);
      try {
        const to = new Date().toISOString().slice(0, 10);
        const from = new Date(Date.now() - (days - 1) * 86400000).toISOString().slice(0, 10);
        const r = await fetch(`${API}/bitacora/roi-per-hour?from=${from}&to=${to}`, { headers: authH });
        if (!r.ok) {
          if (!cancelled) { setData(null); setErrorMsg(`HTTP ${r.status}`); }
          return;
        }
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (err) {
        if (!cancelled) { setData(null); setErrorMsg(err?.message || "Network"); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authH, days]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  if (errorMsg) return <Card data-testid="roi-per-hour-error"><CardContent className="p-8 text-center text-sm text-red-700">Error cargando ROI/hora: {errorMsg}. Reintenta o cambia el rango.</CardContent></Card>;
  if (!data) return <Card data-testid="roi-per-hour-error"><CardContent className="p-8 text-center text-sm text-muted-foreground">Sin datos de ROI/hora disponibles.</CardContent></Card>;

  const items = data.items || [];
  const maxRoi = Math.max(...items.map(i => i.roi_per_hour_eur), 1);

  return (
    <div className="space-y-4" data-testid="roi-per-hour-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-muted-foreground">
          <strong>{data.items_with_value}</strong> ítem(s) con valor · {data.total_hours}h totales del equipo · media <strong>{data.avg_roi_per_hour_eur}€/h</strong>
        </div>
        <div className="inline-flex rounded-md border overflow-hidden" data-testid="roi-range-selector">
          {[28, 90, 180, 365].map(d => (
            <button key={d} onClick={() => setDays(d)}
                    className={`px-2.5 py-1 text-[11px] font-semibold ${days === d ? "bg-emerald-600 text-white" : "hover:bg-emerald-50"}`}
                    data-testid={`roi-range-${d}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {items.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="roi-empty">
          Sin entradas en el rango. Registra horas contra iniciativas del portfolio para calcular el ROI/hora.
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="text-sm w-full">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left px-3 py-2 font-medium">#</th>
                  <th className="text-left px-3 py-2 font-medium">Iniciativa</th>
                  <th className="text-right px-3 py-2 font-medium">Horas</th>
                  <th className="text-right px-3 py-2 font-medium">Valor</th>
                  <th className="text-right px-3 py-2 font-medium">Fuente</th>
                  <th className="text-right px-3 py-2 font-medium">€/hora</th>
                  <th className="text-center px-3 py-2 font-medium">Ranking visual</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const pct = it.roi_per_hour_eur > 0 ? Math.max(4, (it.roi_per_hour_eur / maxRoi) * 100) : 0;
                  const sourceColor = it.value_source === "realized" ? "text-emerald-700 bg-emerald-50" : it.value_source === "expected" ? "text-amber-700 bg-amber-50" : "text-slate-500 bg-slate-50";
                  return (
                    <tr key={`${it.item_type}::${it.item_id}`} className="border-b hover:bg-muted/40" data-testid={`roi-row-${i}`}>
                      <td className="px-3 py-1.5 text-center text-xs text-muted-foreground">{i + 1}</td>
                      <td className="px-3 py-1.5">
                        <div className="font-medium truncate max-w-[280px]" title={it.item_name}>{it.item_name}</div>
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{it.item_type} · {it.contributors} persona(s)</div>
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono">{it.hours}</td>
                      <td className="px-3 py-1.5 text-right font-mono">
                        {new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })
                          .format(it.value_source === "realized" ? it.realized_value_eur : it.expected_value_eur)}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${sourceColor}`}>
                          {it.value_source === "realized" ? "Realizado" : it.value_source === "expected" ? "Esperado" : "Sin valorar"}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono font-bold text-emerald-700">
                        {new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(it.roi_per_hour_eur)}/h
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="h-2 bg-slate-100 rounded overflow-hidden">
                          <div className="h-full bg-emerald-500 rounded" style={{ width: `${pct}%` }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
      <p className="text-[11px] text-muted-foreground italic">
        Fórmula: €/hora = valor realizado (o esperado si no hay realizado) del ítem ÷ horas invertidas del equipo. Requiere xV valorado en cada iniciativa.
      </p>
    </div>
  );
}

export default function ImpactoPage() {
  const { token, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const authH = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("funnel");
  const [configOpen, setConfigOpen] = useState(false);
  const [bookRoi, setBookRoi] = useState(null);

  const load = async () => {
    try {
      const r = await fetch(`${API}/impacto/overview`, { headers: authH });
      if (!r.ok) throw new Error("HTTP " + r.status);
      setData(await r.json());
      // ROI único (petición 2026-08-24: "un único ROI, el del libro, visible en
      // todas las pantallas relevantes") — misma fuente que /dashboard (rd_economics
      // sobre rd_project_balances), para que Impacto no muestre un 4º número distinto.
      try {
        const execR = await fetch(`${API}/dashboard/executive`, { headers: authH });
        if (execR.ok) {
          const exec = await execR.json();
          setBookRoi(exec?.rd_economics || null);
        }
      } catch { /* no bloquea el resto de Impacto si falla */ }
    } catch (e) { toast.error("Error cargando datos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !data) {
    return <div className="p-8 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  }

  const { funnel, roi_directo, eficiencias, cultura, oportunidad_negocio, marca, cumplimiento, config } = data;

  return (
    <div className="p-6 space-y-6" data-testid="impacto-page">
      {/* Hero */}
      <div className="rounded-2xl border bg-gradient-to-br from-emerald-50 via-white to-blue-50 dark:from-emerald-950/30 dark:via-slate-900 dark:to-blue-950/20 p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold mb-1">Innovation OS · Medición</div>
            <h1 className="text-3xl font-bold tracking-tight">Impacto de la Innovación</h1>
            <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
              Cuadro de mando ejecutivo siguiendo el framework de <b>Area101</b>: 7 categorías que traducen la actividad de innovación en euros y decisiones para dirección general.
            </p>
          </div>
          {isAdmin && (
            <Button variant="outline" onClick={() => setConfigOpen(true)} data-testid="impacto-config-btn">
              <PencilLine className="w-4 h-4 mr-2" /> Editar targets y Value 360
            </Button>
          )}
        </div>
        {/* North star KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <NorthStar label="Valor generado (Value 360)" value={eur(roi_directo.value_360.total_value_eur)} sub={`ROI ${roi_directo.value_360.roi_multiple ?? "—"}x sobre budget`} color="emerald" />
          <NorthStar label="% Revenue innovación" value={`${roi_directo.revenue_innovation.current_pct}%`} sub={`Target ${roi_directo.revenue_innovation.target_pct}% (Modelo 3M)`} color={roi_directo.revenue_innovation.on_track ? "emerald" : "amber"} />
          <NorthStar label="Ahorros P&L" value={eur(eficiencias.total_annual_savings_eur)} sub={`${eficiencias.total_co2_avoided_t} tCO₂ evitados`} color="blue" />
          <NorthStar label="Escalados a producción" value={num(funnel.escalados)} sub={`${funnel.conversion_end_to_end_pct}% end-to-end desde exploradas`} color="violet" />
        </div>
      </div>

      <RetroWidget token={token} onOpenTab={setTab} />

      <InnovationFunnelWidget token={token} />

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-12 h-auto">
          <TabsTrigger value="funnel" data-testid="tab-funnel"><Filter className="w-3.5 h-3.5 mr-1.5" />Funnel</TabsTrigger>
          <TabsTrigger value="roi" data-testid="tab-roi"><DollarSign className="w-3.5 h-3.5 mr-1.5" />ROI Directo</TabsTrigger>
          <TabsTrigger value="eficiencias" data-testid="tab-eficiencias"><TrendingUp className="w-3.5 h-3.5 mr-1.5" />Eficiencias</TabsTrigger>
          <TabsTrigger value="cultura" data-testid="tab-cultura"><Users className="w-3.5 h-3.5 mr-1.5" />Cultura</TabsTrigger>
          <TabsTrigger value="oportunidad" data-testid="tab-oportunidad"><Building2 className="w-3.5 h-3.5 mr-1.5" />Negocio</TabsTrigger>
          <TabsTrigger value="marca" data-testid="tab-marca"><Award className="w-3.5 h-3.5 mr-1.5" />Marca</TabsTrigger>
          <TabsTrigger value="plan" data-testid="tab-plan"><Target className="w-3.5 h-3.5 mr-1.5" />Plan</TabsTrigger>
          <TabsTrigger value="val" data-testid="tab-val"><Brain className="w-3.5 h-3.5 mr-1.5" />Decisiones VAL</TabsTrigger>
          <TabsTrigger value="roi-horas" data-testid="tab-roi-horas"><Clock className="w-3.5 h-3.5 mr-1.5" />ROI horas</TabsTrigger>
          <TabsTrigger value="cycle" data-testid="tab-cycle"><Workflow className="w-3.5 h-3.5 mr-1.5" />Ciclo</TabsTrigger>
          <TabsTrigger value="comparativa" data-testid="tab-comparativa"><Scale className="w-3.5 h-3.5 mr-1.5" />Comparativa</TabsTrigger>
          <TabsTrigger value="xv" data-testid="tab-xv"><Rocket className="w-3.5 h-3.5 mr-1.5" />xV · Valor Esperado</TabsTrigger>
        </TabsList>

        {/* 1 · FUNNEL */}
        <TabsContent value="funnel" className="space-y-4">
          <FunnelView f={funnel} />
        </TabsContent>

        {/* 2 · ROI DIRECTO */}
        <TabsContent value="roi" className="space-y-4">
          <RoiView r={roi_directo} bookRoi={bookRoi} />
        </TabsContent>

        {/* 3 · EFICIENCIAS */}
        <TabsContent value="eficiencias" className="space-y-4">
          <EficienciasView e={eficiencias} />
        </TabsContent>

        {/* 4 · CULTURA */}
        <TabsContent value="cultura" className="space-y-4">
          <CulturaView c={cultura} />
        </TabsContent>

        {/* 5 · OPORTUNIDAD */}
        <TabsContent value="oportunidad" className="space-y-4">
          <OportunidadView o={oportunidad_negocio} isAdmin={isAdmin} authH={authH} onChange={load} />
        </TabsContent>

        {/* 6 · MARCA */}
        <TabsContent value="marca" className="space-y-4">
          <MarcaView m={marca} isAdmin={isAdmin} />
        </TabsContent>

        {/* 7 · CUMPLIMIENTO PLAN */}
        <TabsContent value="plan" className="space-y-4">
          <PlanView p={cumplimiento} />
        </TabsContent>

        {/* 8 · DECISIONES VAL — retrospectiva */}
        <TabsContent value="val" className="space-y-4">
          <ValDecisionsView authH={authH} />
        </TabsContent>

        <TabsContent value="roi-horas" className="space-y-4">
          <RoiPerHourView authH={authH} />
        </TabsContent>

        <TabsContent value="cycle" className="space-y-4">
          <InnovationCycleFunnel variant="full" />
        </TabsContent>

        <TabsContent value="comparativa" className="space-y-4">
          <CostByBusinessLine />
        </TabsContent>

        {/* 12 · xV — fusionado dentro de Impacto (2026-08-27), ya no tiene entrada propia en el menú */}
        <TabsContent value="xv" className="space-y-4">
          <Suspense fallback={<div className="p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>}>
            <XVPage />
          </Suspense>
        </TabsContent>
      </Tabs>

      {isAdmin && (
        <ConfigDialog open={configOpen} onOpenChange={setConfigOpen} config={config} authH={authH} onSaved={load} />
      )}
    </div>
  );
}

// ─── Small building blocks ─────────────────────────────────────────────────

function NorthStar({ label, value, sub, color = "emerald" }) {
  const map = {
    emerald: "from-emerald-500 to-emerald-700 text-emerald-50",
    blue: "from-blue-500 to-blue-700 text-blue-50",
    violet: "from-violet-500 to-fuchsia-600 text-violet-50",
    amber: "from-amber-500 to-orange-600 text-amber-50",
  };
  return (
    <div className={`rounded-xl bg-gradient-to-br ${map[color]} p-4 shadow-sm`}>
      <div className="text-[10px] uppercase tracking-widest opacity-90 font-semibold">{label}</div>
      <div className="text-2xl font-bold mt-1 leading-tight">{value}</div>
      <div className="text-xs opacity-90 mt-1">{sub}</div>
    </div>
  );
}

function StatCard({ label, value, sub, testId }) {
  return (
    <div className="rounded-xl border bg-card p-4" data-testid={testId}>
      <div className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{label}</div>
      <div className="text-2xl font-bold mt-1 leading-tight">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

// ─── 1 · Funnel ────────────────────────────────────────────────────────────

function FunnelView({ f }) {
  const stages = [
    { label: "Exploradas", value: f.exploradas, color: "#94a3b8", next: f.introducidas },
    { label: "Introducidas a negocio", value: f.introducidas, color: "#60a5fa", next: f.pilotos_total },
    { label: "Pilotos / POCs", value: f.pilotos_total, color: "#a78bfa", next: f.escalados },
    { label: "Escalados a producción", value: f.escalados, color: "#10b981", next: null },
  ];
  const max = Math.max(...stages.map(s => s.value), 1);
  return (
    <div className="grid gap-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Funnel de Innovación</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {stages.map((s, i) => (
            <div key={s.label}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="font-medium">{s.label}</span>
                <span className="font-mono text-muted-foreground">{num(s.value)}</span>
              </div>
              <div className="h-9 bg-muted rounded-lg overflow-hidden">
                <div className="h-full flex items-center px-3 text-xs font-semibold text-white" style={{ width: `${(s.value / max) * 100}%`, background: s.color, minWidth: s.value > 0 ? "6%" : 0 }}>
                  {s.value > 0 ? `${((s.value / max) * 100).toFixed(0)}%` : ""}
                </div>
              </div>
              {s.next !== null && (
                i === 0 ? (
                  <div className="text-[11px] text-muted-foreground mt-1 ml-1 italic">
                    Poblaciones distintas (partners explorados vs. iniciativas totales) — no se muestra "conversión" entre ambas, la mayoría de iniciativas nace internamente, no de scouting de partners.
                  </div>
                ) : (
                  <div className="text-[11px] text-muted-foreground mt-1 ml-1">
                    ↓ conversión al siguiente estadio: <b>{s.value ? ((s.next / s.value) * 100).toFixed(1) : 0}%</b>
                  </div>
                )
              )}
            </div>
          ))}
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard label="Intro → Piloto" value={`${f.conversion_intro_to_pilot_pct}%`} sub="Iniciativas que llegan a piloto" />
        <StatCard label="Piloto → Escala" value={`${f.conversion_pilot_to_scale_pct}%`} sub="Pilotos escalados a producción" />
        <StatCard label="End-to-end" value={`${f.conversion_end_to_end_pct}%`} sub="Exploradas que acaban escaladas" />
      </div>
    </div>
  );
}

// ─── 2 · ROI Directo ───────────────────────────────────────────────────────

function RoiView({ r, bookRoi }) {
  const v = r.value_360;
  const rev = r.revenue_innovation;
  return (
    <div className="grid gap-4">
      {bookRoi && (
        <Card className="border-emerald-300" data-testid="roi-book-canonical-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-600" /> ROI Histórico I+D (el mismo del Panel)
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Único ROI financiero oficial de la app — libro de balances I+D (<code>rd_project_balances</code>,
              {" "}{bookRoi.project_count} proyectos, sin Barad-dûr). Idéntico al que se ve en /dashboard.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard label="ROI" value={`${bookRoi.roi_percentage}%`} sub="(retorno−invertido)/invertido" />
              <StatCard label="Invertido" value={eur(bookRoi.total_investment)} />
              <StatCard label="Retorno" value={eur(bookRoi.total_benefits)} />
              <StatCard label="Subvención" value={eur(bookRoi.subsidy)} />
            </div>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-600" /> Modelo Value 360 (Moeve)
          </CardTitle>
          <p className="text-xs text-muted-foreground">Retorno econométrico conservador: valor unitario × count real de cada palanca. ROI vs. budget anual.</p>
          <p className="text-[11px] text-amber-600 mt-1" data-testid="roi-impacto-source-note">
            Múltiplo de valor de 7 palancas heterogéneas (partners, iniciativas, pilotos, patentes...) —
            complementario al ROI Histórico I+D oficial de arriba (mismo que /dashboard). Son 2 métricas
            distintas, no deben sumarse ni compararse directamente.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <StatCard label="Valor total generado" value={eur(v.total_value_eur)} />
            <StatCard
              label="Budget anual innovación"
              value={eur(v.annual_budget_eur)}
              sub={v.budget_source === "rd_project_balances_book" ? "= Gasto real I+D (libro), sin configurar" : "configurado"}
            />
            <StatCard label="ROI (múltiplo)" value={v.roi_multiple ? `${v.roi_multiple}x` : "—"} sub={v.roi_multiple ? "Retorno por € invertido" : "Sin datos suficientes"} />
          </div>
          <div className="rounded-lg border overflow-hidden mt-4">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Palanca</th>
                  <th className="text-right px-3 py-2 font-semibold">Valor unitario</th>
                  <th className="text-right px-3 py-2 font-semibold">Count</th>
                  <th className="text-right px-3 py-2 font-semibold">Valor total</th>
                </tr>
              </thead>
              <tbody>
                {v.breakdown.map(row => (
                  <tr key={row.key} className="border-t">
                    <td className="px-3 py-2">{row.label}</td>
                    <td className="px-3 py-2 text-right text-muted-foreground">{eur(row.unit_value_eur)}</td>
                    <td className="px-3 py-2 text-right font-mono">{num(row.count)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{eur(row.value_eur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground" data-testid="value360-breakdown-note">
            Cada fila es una población distinta (partners, iniciativas, pilotos, patentes, subvenciones...)
            — no representa "iniciativas del Portfolio" (170) ni "proyectos PG-07" (57), son universos aparte.
            {v.calculated_at && <> Calculado el {new Date(v.calculated_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}.</>}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Rocket className="w-4 h-4 text-blue-600" /> % Revenue de Innovación (Modelo 3M)
          </CardTitle>
          <p className="text-xs text-muted-foreground">Objetivo 3M: al menos <b>{rev.target_pct}%</b> del revenue anual debe proceder de productos/servicios nuevos.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-4 gap-3">
            <StatCard label="Actual" value={`${rev.current_pct}%`} />
            <StatCard label="Target 3M" value={`${rev.target_pct}%`} />
            <StatCard label="Gap" value={`${rev.gap_pct > 0 ? "+" : ""}${rev.gap_pct}%`} sub={rev.on_track ? "En objetivo" : "Bajo objetivo"} />
            <StatCard label="Revenue innovación" value={eur(rev.innovation_revenue_eur)} sub={`de ${eur(rev.annual_revenue_total_eur)} total`} />
          </div>
          <div className="mt-2">
            <Progress value={Math.min(100, (rev.current_pct / (rev.target_pct || 1)) * 100)} className="h-3" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── 3 · Eficiencias ───────────────────────────────────────────────────────

function EficienciasView({ e }) {
  const ttm = e.time_to_market;
  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard label="Ahorros anuales P&L" value={eur(e.total_annual_savings_eur)} sub={`Producción + pilotos realizados${e.has_estimado ? " · incluye ESTIMADO" : ""}`} />
        <StatCard label="CO₂ evitado" value={`${e.total_co2_avoided_t} tCO₂`} />
        <StatCard label="Time-to-Market medio" value={ttm.average_months ? `${ttm.average_months} m` : "—"} sub={`Target ${ttm.target_months} m · ${ttm.samples} muestras · ${ttm.on_track ? "en objetivo ✅" : "por encima ⚠️"}`} />
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">Ahorros por producción</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {e.savings_by_production.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Sin producciones activas todavía.</p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={e.savings_by_production}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => eur(v)} />
                  <Bar dataKey="savings_eur" fill="#10b981" name="Ahorro anual (€)" />
                </BarChart>
              </ResponsiveContainer>
              {e.savings_by_production.some(p => p.is_estimado) && (
                <p className="text-[11px] text-amber-600" data-testid="eficiencias-estimado-note">
                  ⚠ {e.savings_by_production.filter(p => p.is_estimado).map(p => p.name).join(", ")}: cifras marcadas como ESTIMADO en la ficha de producción (no medidas).
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── 4 · Cultura ───────────────────────────────────────────────────────────

function CulturaView({ c }) {
  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard label="Empleados impactados" value={num(c.employees_impacted)} sub={`de ${num(c.employees_total)} activos`} />
        <StatCard label="Engagement" value={`${c.engagement_pct}%`} sub="Con actividad en la plataforma" />
        <StatCard label="NPS Business Units" value={c.nps_business_units ? c.nps_business_units.nps : "—"} sub={c.nps_business_units ? `n=${c.nps_business_units.n} · promotores ${c.nps_business_units.promoters_pct}%` : "Lanza una encuesta NPS"} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-purple-600" /> NPS de Innovación por BU
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Mide la percepción del área de Innovación por parte de las Business Units. Crea una encuesta con título que contenga &quot;NPS Innovación&quot; en el módulo Encuestas.
          </p>
        </CardHeader>
        <CardContent>
          {(c.nps_surveys_available || []).length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center">
              <p className="text-sm text-muted-foreground mb-3">Aún no has lanzado una encuesta NPS de innovación.</p>
              <Button variant="outline" size="sm" onClick={() => window.location.href = "/surveys"}>
                <ArrowRight className="w-3.5 h-3.5 mr-1.5" /> Crear encuesta en /surveys
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {(c.nps_surveys_available || []).map(s => (
                <div key={s.id} className="flex items-center justify-between p-2.5 rounded-lg border">
                  <div>
                    <p className="text-sm font-medium">{s.title}</p>
                    <p className="text-xs text-muted-foreground">{s.responses_count || 0} respuestas</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => window.location.href = "/surveys"}>Ver</Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── 5 · Oportunidad de Negocio ────────────────────────────────────────────

function OportunidadView({ o, isAdmin, authH, onChange }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const openNew = () => { setEditing({ title: "", kind: "licitacion", amount_eur: 0, status: "pipeline", innovation_attribution_pct: 100, date: new Date().toISOString().slice(0, 10), notes: "" }); setDialogOpen(true); };
  const openEdit = (o) => { setEditing({ ...o }); setDialogOpen(true); };
  const save = async () => {
    const isNew = !editing.id;
    const url = isNew ? `${API}/impacto/opportunities` : `${API}/impacto/opportunities/${editing.id}`;
    const r = await fetch(url, { method: isNew ? "POST" : "PUT", headers: { ...authH, "Content-Type": "application/json" }, body: JSON.stringify(editing) });
    if (!r.ok) { toast.error("Error guardando"); return; }
    toast.success(isNew ? "Oportunidad creada" : "Actualizada");
    setDialogOpen(false); onChange();
  };
  const remove = async (id) => {
    if (!confirm("¿Eliminar esta oportunidad?")) return;
    const r = await fetch(`${API}/impacto/opportunities/${id}`, { method: "DELETE", headers: authH });
    if (!r.ok) { toast.error("Error"); return; }
    onChange();
  };
  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Ganadas" value={num(o.won_count)} sub={eur(o.won_amount_eur)} />
        <StatCard label="En pipeline" value={num(o.pipeline_count)} sub={eur(o.pipeline_amount_eur)} />
        <StatCard label="Win rate" value={`${o.win_rate_pct}%`} sub={`${o.won_count} won / ${o.lost_count} lost`} />
        <StatCard label="Perdidas" value={num(o.lost_count)} />
      </div>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Licitaciones y contratos vía ecosistema</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Registra oportunidades de negocio atribuibles a la innovación (licitaciones ganadas gracias a partnerships, nuevos clientes, contratos marco).</p>
            </div>
            {isAdmin && <Button size="sm" onClick={openNew} data-testid="opp-new-btn"><Plus className="w-3.5 h-3.5 mr-1.5" />Nueva</Button>}
          </div>
        </CardHeader>
        <CardContent>
          {o.items.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Aún no hay oportunidades registradas.</p>
          ) : (
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-3 py-2">Título</th>
                    <th className="text-left px-3 py-2">Tipo</th>
                    <th className="text-right px-3 py-2">Importe</th>
                    <th className="text-center px-3 py-2">Estado</th>
                    <th className="text-right px-3 py-2 w-24">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {o.items.map(it => (
                    <tr key={it.id} className="border-t hover:bg-muted/30">
                      <td className="px-3 py-2 font-medium">{it.title}</td>
                      <td className="px-3 py-2"><Badge variant="outline">{it.kind}</Badge></td>
                      <td className="px-3 py-2 text-right font-mono">{eur(it.amount_eur)}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge variant={it.status === "won" ? "default" : it.status === "lost" ? "destructive" : "secondary"}>{it.status}</Badge>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isAdmin && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => openEdit(it)}><PencilLine className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => remove(it.id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Editar oportunidad" : "Nueva oportunidad"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Título</Label><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="opp-title" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo</Label>
                  <select className="w-full border rounded-md px-3 py-2 bg-background text-sm" value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                    <option value="licitacion">Licitación</option>
                    <option value="cliente">Nuevo cliente</option>
                    <option value="contrato_marco">Contrato marco</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
                <div>
                  <Label>Estado</Label>
                  <select className="w-full border rounded-md px-3 py-2 bg-background text-sm" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                    <option value="pipeline">Pipeline</option>
                    <option value="in_progress">En curso</option>
                    <option value="won">Ganada</option>
                    <option value="lost">Perdida</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Importe (€)</Label><Input type="number" value={editing.amount_eur} onChange={(e) => setEditing({ ...editing, amount_eur: parseFloat(e.target.value) || 0 })} /></div>
                <div><Label>Atribución innovación (%)</Label><Input type="number" min="0" max="100" value={editing.innovation_attribution_pct} onChange={(e) => setEditing({ ...editing, innovation_attribution_pct: parseFloat(e.target.value) || 0 })} /></div>
              </div>
              <div><Label>Fecha</Label><Input type="date" value={(editing.date || "").slice(0, 10)} onChange={(e) => setEditing({ ...editing, date: e.target.value })} /></div>
              <div><Label>Notas</Label><Input value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="opp-save"><Save className="w-3.5 h-3.5 mr-1.5" />Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── 6 · Marca ─────────────────────────────────────────────────────────────

function MarcaView({ m }) {
  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Menciones en medios" value={num(m.media_mentions)} />
        <StatCard label="Premios" value={num(m.awards)} />
        <StatCard label="Certificaciones" value={num(m.certifications)} sub="ISO 56001, ESG…" />
        <StatCard label="Talento atraído" value={num(m.talent_attracted)} sub="Contrataciones vía innovación" />
        <StatCard label="Seguidores LinkedIn" value={num(m.linkedin_followers)} />
      </div>
      <p className="text-xs text-muted-foreground italic px-1">Los valores de marca se editan desde <b>Editar targets</b> (arriba a la derecha).</p>
    </div>
  );
}

// ─── 7 · Cumplimiento Plan ─────────────────────────────────────────────────

function PlanView({ p }) {
  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard label="% hitos completados" value={`${p.plan_progress_pct}%`} sub={`${p.milestones_done} / ${p.milestones_total}`} />
        <StatCard label="TRL medio cartera" value={p.trl_average ?? "—"} sub="Escala 1-9" />
        <StatCard label="Iniciativas totales" value={num(p.trl_distribution.reduce((a, b) => a + b.count, 0))} />
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">Distribución TRL de la cartera</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={p.trl_distribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="trl" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#8b5cf6" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Hitos del plan estratégico</CardTitle></CardHeader>
        <CardContent>
          {p.milestones.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Sin hitos configurados. Un admin puede añadirlos desde &quot;Editar targets&quot;.</p>
          ) : (
            <div className="space-y-2">
              {p.milestones.map((m, i) => (
                <div key={m.id || i} className="flex items-center gap-3 p-2.5 rounded-lg border">
                  {m.done ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <XCircle className="w-4 h-4 text-muted-foreground shrink-0" />}
                  <div className="flex-1">
                    <p className="text-sm font-medium">{m.name}</p>
                    {m.target_date && <p className="text-xs text-muted-foreground">Objetivo: {m.target_date}</p>}
                  </div>
                  <Badge variant="outline" className="text-xs">peso {m.weight || 1}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── 8 · Decisiones VAL — retrospectiva ────────────────────────────────────

const HEALTH_RANGE_OPTIONS = [4, 8, 12, 24];

function ValHealthTrend({ authH, onOpenLatest }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [range, setRange] = useState(12);
  const [showCapital, setShowCapital] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/assistant/retro/history?weeks=${range}`, { headers: authH });
        if (!r.ok) throw new Error("HTTP " + r.status);
        const j = await r.json();
        if (!cancelled) { setHistory(j); setError(false); }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authH, range]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return null;

  if (error) {
    return (
      <Card className="border-amber-200/60 bg-amber-50/40 dark:bg-amber-950/10 dark:border-amber-800/40" data-testid="val-health-error">
        <CardContent className="p-4 flex items-center gap-3 text-sm text-amber-800 dark:text-amber-300">
          <XCircle className="w-4 h-4 flex-shrink-0" /> No se pudo cargar el histórico de salud VAL.
        </CardContent>
      </Card>
    );
  }
  if (!history || history.count === 0) {
    return (
      <Card data-testid="val-health-trend">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-purple-600" /> Salud VAL · Serie temporal
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-6">
            Aún no hay retrospectivas guardadas. La primera se generará el próximo viernes 18:00.
          </p>
        </CardContent>
      </Card>
    );
  }

  let cumMoved = 0;
  const points = history.points.map(p => {
    const dt = p.generated_at ? new Date(p.generated_at) : null;
    cumMoved += Number(p.total_moved_week_eur || 0);
    return {
      label: dt ? `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}` : "—",
      hitRate: p.hit_rate_pct,
      decisions: p.decisions_this_week,
      moved: p.total_moved_week_eur,
      cumMoved,
      drop: p.drop_alert_sent,
      rise: p.rise_alert_sent,
    };
  });
  const last = points[points.length - 1] || {};
  const first = points[0] || {};
  const cumDelta = last.hitRate != null && first.hitRate != null ? +(last.hitRate - first.hitRate).toFixed(1) : null;

  return (
    <Card data-testid="val-health-trend">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-purple-600" /> Salud VAL · Últimas {history.count} semanas
          </CardTitle>
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="val-health-range-selector">
              {HEALTH_RANGE_OPTIONS.map(w => (
                <button
                  key={w}
                  type="button"
                  onClick={() => setRange(w)}
                  disabled={loading}
                  data-testid={`val-health-range-${w}`}
                  className={`px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                    range === w
                      ? "bg-purple-600 text-white"
                      : "bg-transparent text-muted-foreground hover:bg-purple-50 dark:hover:bg-purple-950/30"
                  }`}
                >
                  {w}sem
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setShowCapital(v => !v)}
              data-testid="val-health-capital-toggle"
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md border text-[11px] font-semibold transition-colors ${
                showCapital
                  ? "bg-emerald-600 text-white border-emerald-600"
                  : "border-border text-muted-foreground hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
              }`}
              title="Overlay del capital reasignado acumulado"
            >
              <DollarSign className="w-3 h-3" /> Capital
            </button>
            <span className="text-muted-foreground">Δ:</span>
            <Badge className={cumDelta > 0 ? "bg-emerald-100 text-emerald-700" : cumDelta < 0 ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}
                   data-testid="val-health-cum-delta">
              {cumDelta === null ? "—" : cumDelta > 0 ? `+${cumDelta} pp` : `${cumDelta} pp`}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={points} margin={{ top: 10, right: showCapital ? 60 : 10, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id="cumCapitalGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.25} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false}
                   tickFormatter={v => `${v}%`} domain={[0, 100]} width={38} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={24} />
            {showCapital && (
              <YAxis yAxisId="capital" orientation="right" tick={{ fontSize: 10, fill: "#10b981" }} axisLine={false} tickLine={false}
                     tickFormatter={v => `${(v / 1000).toFixed(0)}k€`} width={48} />
            )}
            <Tooltip content={<HealthTooltip />} />
            <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "6px" }} formatter={healthLegendFormatter} />
            {showCapital && (
              <Area
                yAxisId="capital"
                type="monotone"
                dataKey="cumMoved"
                name="Capital acum."
                stroke="#10b981"
                strokeWidth={1.5}
                fill="url(#cumCapitalGradient)"
              />
            )}
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="hitRate"
              name="Hit-rate %"
              stroke="#7c3aed"
              strokeWidth={2.5}
              dot={<HitRateDot />}
              activeDot={{ r: 6, strokeWidth: 0 }}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="decisions"
              name="Decisiones sem."
              stroke="#94a3b8"
              strokeWidth={1.5}
              strokeDasharray="4 3"
              dot={{ r: 3, fill: "#94a3b8", strokeWidth: 0 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
        <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Alerta subida ≥15 pp</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Alerta caída ≥15 pp</span>
          <span className="text-muted-foreground/70">Los puntos coloreados marcan las semanas donde se disparó una alerta a Teams.</span>
        </p>
      </CardContent>
    </Card>
  );
}

// Module-scope helpers to avoid `no-unstable-nested-components`
const HealthLegendLabel = ({ value }) => <span style={{ color: "hsl(var(--foreground))" }}>{value}</span>;
const healthLegendFormatter = (value) => <HealthLegendLabel value={value} />;

const HealthTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload || {};
  const fmt = (v) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);
  return (
    <div className="bg-popover border border-border rounded-lg shadow-xl p-3 text-xs">
      <p className="font-semibold mb-1">{label}</p>
      <p className="text-purple-600 font-mono font-medium">Hit-rate: {p.hitRate ?? "—"}%</p>
      <p className="text-slate-500 font-mono">Decisiones: {p.decisions ?? 0}</p>
      <p className="text-slate-500 font-mono">Movido: {fmt(p.moved)}</p>
      {p.cumMoved != null && <p className="text-emerald-600 font-mono">Capital acum.: {fmt(p.cumMoved)}</p>}
      {p.drop && <p className="text-red-600 mt-1">🚨 Alerta caída disparada</p>}
      {p.rise && <p className="text-emerald-600 mt-1">🎉 Alerta subida disparada</p>}
    </div>
  );
};

const HitRateDot = (props) => {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null) return null;
  if (payload?.drop) return <circle cx={cx} cy={cy} r={5} fill="#dc2626" stroke="#fff" strokeWidth={2} />;
  if (payload?.rise) return <circle cx={cx} cy={cy} r={5} fill="#10b981" stroke="#fff" strokeWidth={2} />;
  return <circle cx={cx} cy={cy} r={3} fill="#7c3aed" />;
};

function ValDecisionsView({ authH }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/impacto/rebalance-history`, { headers: authH });
        if (r.ok) setData(await r.json());
      } catch { toast.error("Error cargando historial"); }
      finally { setLoading(false); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  if (!data || data.total_decisions === 0) {
    return (
      <div className="grid gap-4">
        <ValHealthTrend authH={authH} />
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Brain className="w-4 h-4 text-violet-600" /> Retrospectiva de decisiones aplicadas por VAL
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border border-dashed p-8 text-center">
              <p className="text-sm text-muted-foreground mb-1">Sin decisiones aplicadas todavía.</p>
              <p className="text-xs text-muted-foreground">Cuando aceptes una reasignación propuesta por VAL (action commit_rebalance), aparecerá aquí con métricas de acierto retrospectivo.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const verdictConfig = {
    hit: { color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2, label: "Acierto" },
    neutral: { color: "bg-amber-100 text-amber-800", icon: MinusCircle, label: "Neutral" },
    miss: { color: "bg-red-100 text-red-800", icon: XCircle, label: "Fallo" },
    "n/a": { color: "bg-slate-100 text-slate-600", icon: MinusCircle, label: "N/A" },
  };

  return (
    <div className="grid gap-4">
      <ValHealthTrend authH={authH} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Decisiones aplicadas" value={num(data.total_decisions)} sub="Por VAL Copilot" />
        <StatCard label="Capital reasignado" value={eur(data.total_moved_eur)} />
        <StatCard label="Hit-rate retrospectivo" value={`${data.hit_rate_pct}%`} sub={`${data.proposals_hit}/${data.proposals_scored} palancas mejoraron`} />
        <StatCard label="Modelo de aprendizaje" value="Activo" sub="VAL aprende de cada decisión" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Brain className="w-4 h-4 text-violet-600" /> Timeline de decisiones aplicadas
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Cada decisión compara el <b>count</b> de la palanca destino al momento de aplicar vs. el actual.
            El veredicto se basa en si el número creció (hit), se mantuvo (neutral) o bajó (miss).
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-2" data-testid="val-decisions-list">
            {data.items.map((item) => {
              const isOpen = expanded === item.id;
              return (
                <div key={item.id} className="rounded-lg border overflow-hidden">
                  <div className="p-3 flex items-center gap-3 hover:bg-muted/40 cursor-pointer" onClick={() => setExpanded(isOpen ? null : item.id)}>
                    <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                      <Brain className="w-5 h-5 text-violet-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold">{item.proposals.length} reasignaciones · {eur(item.total_moved_eur)}</p>
                        {item.log_hit_rate_pct !== null && (
                          <Badge variant="outline" className={item.log_hit_rate_pct >= 60 ? "bg-emerald-50 text-emerald-800 border-emerald-200" : item.log_hit_rate_pct >= 30 ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-red-50 text-red-800 border-red-200"}>
                            Hit-rate {item.log_hit_rate_pct}%
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {item.user_email} · {new Date(item.applied_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })} ({item.days_since_applied} días)
                      </p>
                    </div>
                    <Button variant="ghost" size="sm">{isOpen ? "Cerrar" : "Ver detalle"}</Button>
                  </div>
                  {isOpen && (
                    <div className="border-t bg-violet-50/30 p-3 space-y-2">
                      {item.proposals.map((p, i) => {
                        const cfg = verdictConfig[p.verdict] || verdictConfig["n/a"];
                        const V = cfg.icon;
                        return (
                          <div key={i} className="rounded border bg-white p-2.5">
                            <div className="flex items-center gap-2 flex-wrap mb-1.5">
                              <span className="font-semibold text-sm text-slate-800 truncate">{p.from_category}</span>
                              <ArrowRight className="w-3.5 h-3.5 text-violet-500" />
                              <span className="font-semibold text-sm text-slate-800 truncate">{p.to_lever}</span>
                              <span className="ml-auto text-sm font-mono font-bold">{eur(p.amount_eur)}</span>
                              <span className={`px-2 py-0.5 rounded text-[11px] font-semibold flex items-center gap-1 ${cfg.color}`}>
                                <V className="w-3 h-3" /> {cfg.label}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mb-2">{p.rationale}</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                              <div className="rounded bg-slate-50 p-1.5">
                                <p className="text-muted-foreground uppercase text-[9px] tracking-wider">Al aplicar</p>
                                <p className="font-mono font-semibold">{num(p.count_at_apply)} uds</p>
                              </div>
                              <div className="rounded bg-slate-50 p-1.5">
                                <p className="text-muted-foreground uppercase text-[9px] tracking-wider">Hoy</p>
                                <p className="font-mono font-semibold">{num(p.count_now)} uds</p>
                              </div>
                              <div className="rounded bg-slate-50 p-1.5">
                                <p className="text-muted-foreground uppercase text-[9px] tracking-wider">Δ palanca</p>
                                <p className={`font-mono font-semibold ${p.delta_units > 0 ? "text-emerald-600" : p.delta_units < 0 ? "text-red-600" : "text-slate-600"}`}>
                                  {p.delta_units > 0 ? "+" : ""}{p.delta_units}
                                </p>
                              </div>
                              <div className="rounded bg-slate-50 p-1.5">
                                <p className="text-muted-foreground uppercase text-[9px] tracking-wider">Valor realizado</p>
                                <p className="font-mono font-semibold">{eur(p.realized_value_eur)} <span className="text-[9px] text-muted-foreground">({p.realized_pct}% del esperado)</span></p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Config Dialog (admin) ─────────────────────────────────────────────────

function ConfigDialog({ open, onOpenChange, config, authH, onSaved }) {
  const [cfg, setCfg] = useState(config);
  useEffect(() => { setCfg(config); }, [config, open]);
  const save = async () => {
    const payload = {
      value_360: cfg.value_360,
      target_innovation_revenue_pct: Number(cfg.target_innovation_revenue_pct),
      annual_revenue_total_eur: Number(cfg.annual_revenue_total_eur),
      innovation_revenue_current_eur: Number(cfg.innovation_revenue_current_eur),
      time_to_market_target_months: Number(cfg.time_to_market_target_months),
      annual_innovation_budget_eur: Number(cfg.annual_innovation_budget_eur),
      plan_milestones: cfg.plan_milestones,
      brand: cfg.brand,
    };
    const r = await fetch(`${API}/impacto/config`, { method: "PUT", headers: { ...authH, "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    if (!r.ok) { toast.error("Error guardando"); return; }
    toast.success("Configuración guardada");
    onOpenChange(false); onSaved();
  };

  if (!cfg) return null;

  const updateBrand = (k, v) => setCfg({ ...cfg, brand: { ...cfg.brand, [k]: Number(v) || 0 } });
  const updateValue360 = (i, v) => { const arr = [...cfg.value_360]; arr[i] = { ...arr[i], unit_value_eur: Number(v) || 0 }; setCfg({ ...cfg, value_360: arr }); };
  const addMilestone = () => setCfg({ ...cfg, plan_milestones: [...(cfg.plan_milestones || []), { id: crypto.randomUUID(), name: "", weight: 1, done: false, target_date: "" }] });
  const updateMilestone = (i, patch) => { const arr = [...(cfg.plan_milestones || [])]; arr[i] = { ...arr[i], ...patch }; setCfg({ ...cfg, plan_milestones: arr }); };
  const removeMilestone = (i) => { const arr = [...(cfg.plan_milestones || [])]; arr.splice(i, 1); setCfg({ ...cfg, plan_milestones: arr }); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Configuración de Impacto</DialogTitle></DialogHeader>
        <div className="space-y-6">

          <section>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><DollarSign className="w-4 h-4 text-emerald-600" /> Modelo 3M · % Revenue Innovación</h3>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Target % (Modelo 3M)</Label><Input type="number" value={cfg.target_innovation_revenue_pct} onChange={(e) => setCfg({ ...cfg, target_innovation_revenue_pct: e.target.value })} /></div>
              <div><Label>Revenue total anual (€)</Label><Input type="number" value={cfg.annual_revenue_total_eur} onChange={(e) => setCfg({ ...cfg, annual_revenue_total_eur: e.target.value })} /></div>
              <div><Label>Revenue innovación actual (€)</Label><Input type="number" value={cfg.innovation_revenue_current_eur} onChange={(e) => setCfg({ ...cfg, innovation_revenue_current_eur: e.target.value })} /></div>
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4 text-emerald-600" /> Modelo Value 360 · valores unitarios (€)</h3>
            <div className="space-y-2">
              {cfg.value_360.map((row, i) => (
                <div key={row.key} className="grid grid-cols-3 gap-2 items-center">
                  <Label className="col-span-2">{row.label} <span className="text-xs text-muted-foreground">({row.source})</span></Label>
                  <Input type="number" value={row.unit_value_eur} onChange={(e) => updateValue360(i, e.target.value)} />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div><Label>Budget anual de innovación (€)</Label><Input type="number" value={cfg.annual_innovation_budget_eur} onChange={(e) => setCfg({ ...cfg, annual_innovation_budget_eur: e.target.value })} /></div>
              <div><Label>Time-to-Market target (meses)</Label><Input type="number" value={cfg.time_to_market_target_months} onChange={(e) => setCfg({ ...cfg, time_to_market_target_months: e.target.value })} /></div>
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><Award className="w-4 h-4 text-amber-600" /> Marca</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div><Label>Menciones medios</Label><Input type="number" value={cfg.brand?.media_mentions || 0} onChange={(e) => updateBrand("media_mentions", e.target.value)} /></div>
              <div><Label>Premios</Label><Input type="number" value={cfg.brand?.awards || 0} onChange={(e) => updateBrand("awards", e.target.value)} /></div>
              <div><Label>Certificaciones</Label><Input type="number" value={cfg.brand?.certifications || 0} onChange={(e) => updateBrand("certifications", e.target.value)} /></div>
              <div><Label>Talento atraído</Label><Input type="number" value={cfg.brand?.talent_attracted || 0} onChange={(e) => updateBrand("talent_attracted", e.target.value)} /></div>
              <div><Label>Seguidores LinkedIn</Label><Input type="number" value={cfg.brand?.linkedin_followers || 0} onChange={(e) => updateBrand("linkedin_followers", e.target.value)} /></div>
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold flex items-center gap-2"><Target className="w-4 h-4 text-violet-600" /> Hitos del plan estratégico</h3>
              <Button variant="outline" size="sm" onClick={addMilestone}><Plus className="w-3.5 h-3.5 mr-1" />Añadir</Button>
            </div>
            <div className="space-y-2">
              {(cfg.plan_milestones || []).map((m, i) => (
                <div key={m.id} className="grid grid-cols-[1fr_80px_120px_80px_40px] gap-2 items-center">
                  <Input placeholder="Nombre del hito" value={m.name} onChange={(e) => updateMilestone(i, { name: e.target.value })} />
                  <Input type="number" placeholder="Peso" value={m.weight} onChange={(e) => updateMilestone(i, { weight: Number(e.target.value) || 1 })} />
                  <Input type="date" value={m.target_date || ""} onChange={(e) => updateMilestone(i, { target_date: e.target.value })} />
                  <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={!!m.done} onChange={(e) => updateMilestone(i, { done: e.target.checked })} />Hecho</label>
                  <Button variant="ghost" size="icon" onClick={() => removeMilestone(i)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                </div>
              ))}
              {(!cfg.plan_milestones || cfg.plan_milestones.length === 0) && (
                <p className="text-xs text-muted-foreground italic">Sin hitos. Añade los hitos clave del plan de innovación.</p>
              )}
            </div>
          </section>

        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} data-testid="impacto-config-save"><Save className="w-4 h-4 mr-1.5" />Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
